import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';

const setupEmbla = () => {
  const nodes = document.querySelectorAll('.embla');

  for (const node of nodes) {
    const viewportNode = node.querySelector('.embla__viewport');
    if (!viewportNode) continue;

    // Count slides before init — used for centering and loop decisions.
    const slideCount = node.querySelectorAll('.embla__slide').length;

    // Read --max-slides CSS custom property, which changes per breakpoint.
    // This keeps breakpoint values in CSS rather than duplicating them in JS.
    const getMaxSlides = () =>
      Number.parseInt(
        getComputedStyle(node).getPropertyValue('--max-slides').trim(),
        10,
      ) || 1;

    // Slides fit within the visible area — no scrolling or looping needed.
    const shouldCenter = () => slideCount <= getMaxSlides();

    const options = {
      loop: !shouldCenter(),
      duration: 40,
      // 1 slide per scroll ensures every slide gets its own snap point,
      // which means one indicator dot per slide on all screen sizes.
      slidesToScroll: 1,
      watchDrag: !shouldCenter(),
    };

    const plugins = [
      Autoplay({
        delay: 4000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    ];

    const emblaApi = EmblaCarousel(viewportNode, options, plugins);

    // Toggle centering class and re-init embla when the viewport width crosses a breakpoint.
    let currentLoop = options.loop;
    const updateSlider = () => {
      const centering = shouldCenter();
      node.classList.toggle('embla--centered', centering);

      const nextLoop = !centering;
      if (currentLoop !== nextLoop) {
        currentLoop = nextLoop;
        emblaApi.reInit({
          loop: nextLoop,
          watchDrag: nextLoop,
        });
      }

      // Hide controls if there's nothing to scroll.
      const controls = node.querySelector('.embla__controls');
      if (controls) {
        const canScroll = emblaApi.canScrollNext() || emblaApi.canScrollPrev();
        controls.style.display = canScroll ? '' : 'none';
      }
    };

    updateSlider();

    // ResizeObserver rechecks state whenever the embla element is resized.
    const resizeObserver = new ResizeObserver(updateSlider);
    resizeObserver.observe(node);

    // Prev / Next buttons.
    const prevBtn = node.querySelector('.embla__button--prev');
    const nextBtn = node.querySelector('.embla__button--next');

    if (prevBtn && nextBtn) {
      const togglePrevNextButtonsState = () => {
        prevBtn.classList.toggle(
          'embla__button--disabled',
          !emblaApi.canScrollPrev(),
        );
        nextBtn.classList.toggle(
          'embla__button--disabled',
          !emblaApi.canScrollNext(),
        );
      };

      prevBtn.addEventListener('click', () => emblaApi.scrollPrev(), false);
      nextBtn.addEventListener('click', () => emblaApi.scrollNext(), false);

      emblaApi
        .on('select', togglePrevNextButtonsState)
        .on('reinit', togglePrevNextButtonsState)
        .on('init', togglePrevNextButtonsState);

      togglePrevNextButtonsState();
    }

    // Pagination dots.
    const paginationNode = node.querySelector('.embla__dots');
    if (paginationNode) {
      let dots = [];

      const addDotBtnsWithClickHandlers = () => {
        paginationNode.innerHTML = '';
        dots = emblaApi.scrollSnapList().map((_, index) => {
          const dot = document.createElement('button');
          dot.classList.add('embla__dot');
          dot.type = 'button';
          dot.addEventListener('click', () => emblaApi.scrollTo(index), false);
          paginationNode.appendChild(dot);
          return dot;
        });
      };

      const updatePagination = () => {
        const selected = emblaApi.selectedScrollSnap();
        dots.forEach((dot, index) => {
          dot.classList.toggle('embla__dot--active', index === selected);
        });
      };

      emblaApi
        .on('init', () => {
          addDotBtnsWithClickHandlers();
          updatePagination();
          updateSlider(); // Re-check visibility after init/reinit
        })
        .on('reinit', () => {
          addDotBtnsWithClickHandlers();
          updatePagination();
          updateSlider();
        })
        .on('select', updatePagination);

      addDotBtnsWithClickHandlers();
      updatePagination();
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  setupEmbla();
});
